import React, { useState } from "react";
import { Table, Tabs, Card, Text, Badge } from "@radix-ui/themes";
import { Button } from "../../ds2/components";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  CopyIcon,
} from "@radix-ui/react-icons";
import { Employee } from "winslow-core";

import "./EmployeesTable.css";

interface EmployeesTableProps {
  employees: Employee[];
}

export const EmployeesTable: React.FC<EmployeesTableProps> = ({
  employees,
}) => {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<"profile" | "expanded" | "local">(
    "profile"
  );

  const formatDate = (dateString: Date | string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };

  const toggleRow = (employeeId: string) => {
    const newExpandedRows = new Set(expandedRows);

    if (newExpandedRows.has(employeeId)) {
      newExpandedRows.delete(employeeId);
    } else {
      newExpandedRows.add(employeeId);
    }

    setExpandedRows(newExpandedRows);
  };

  const isUUID = (value: string) => {
    const uuidRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex.test(value);
  };

  const formatValue = (value: any) => {
    if (value === null || value === undefined) return "N/A";
    if (value instanceof Date) return formatDate(value);
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (typeof value === "object" && value !== null) {
      return JSON.stringify(value, null, 2);
    }
    return String(value);
  };

  const renderStyledData = (data: any) => {
    const filteredData = Object.entries(data).filter(([key, value]) => {
      // Filter out UUIDs
      if (typeof value === "string" && isUUID(value)) return false;
      // Filter out the data field - it will be displayed separately as HRIS data
      if (key === "data") return false;
      return true;
    });

    return (
      <div className="employee-details-styled">
        {filteredData.map(([key, value]) => (
          <div key={key} className="employee-detail-row">
            <Text weight="medium" className="employee-detail-label">
              {key.charAt(0).toUpperCase() +
                key.slice(1).replace(/([A-Z])/g, " $1")}
            </Text>
            <Text
              className={`employee-detail-value ${typeof value === "object" && value !== null ? "object-value" : ""} ${key.toLowerCase() === "avatar" ? "avatar-value" : ""}`}>
              {formatValue(value)}
            </Text>
          </div>
        ))}
        {/* Display HRIS data field separately if it exists */}
        {data.data && typeof data.data === "object" && (
          <div className="employee-detail-row">
            <Text weight="medium" className="employee-detail-label">
              HRIS Data
            </Text>
            <Text className="employee-detail-value hris-data">
              {JSON.stringify(data.data, null, 2).length > 200
                ? `${JSON.stringify(data.data, null, 2).substring(0, 200)}...`
                : JSON.stringify(data.data, null, 2)}
            </Text>
          </div>
        )}
      </div>
    );
  };

  const renderHRISData = (data: any) => {
    const sections = [
      {
        title: "Basic Information",
        fields: [
          "id",
          "remoteId",
          "employeeNumber",
          "firstName",
          "lastName",
          "displayFullName",
          "workEmail",
          "startDate",
          "employmentStatus",
          "avatar",
          "createdAt",
          "modifiedAt",
          "remoteWasDeleted",
        ],
      },
      {
        title: "Employments",
        data: data.employments || [],
        isArray: true,
      },
      {
        title: "Groups",
        data: data.groups || [],
        isArray: true,
      },
      {
        title: "Work Location",
        data: data.workLocation,
        isObject: true,
      },
      {
        title: "Home Location",
        data: data.homeLocation,
        isObject: true,
      },
      {
        title: "Field Mappings",
        data: data.fieldMappings,
        isObject: true,
      },
    ];

    return (
      <div className="hris-data-container">
        {sections.map((section, index) => (
          <div key={index} className="hris-section">
            <div className="hris-section-header">
              <Text size="4" weight="bold" className="hris-section-title">
                {section.title}
              </Text>
              {section.data && (
                <Button
                  
                  onClick={() =>
                    copyToClipboard(
                      JSON.stringify(section.data, null, 2),
                      section.title
                    )
                  }
                  className="copy-button"
                >
                  <CopyIcon width="14" height="14" />
                </Button>
              )}
            </div>

            {section.fields && (
              <div className="employee-details-styled">
                {section.fields.map((field) => (
                  <div key={field} className="employee-detail-row">
                    <Text weight="medium" className="employee-detail-label">
                      {field.charAt(0).toUpperCase() +
                        field.slice(1).replace(/([A-Z])/g, " $1")}
                    </Text>
                    <Text
                      className={`employee-detail-value ${typeof data[field] === "object" && data[field] !== null ? "object-value" : ""} ${field.toLowerCase() === "avatar" ? "avatar-value" : ""}`}>
                      {formatValue(data[field])}
                    </Text>
                  </div>
                ))}
              </div>
            )}

            {section.isArray && Array.isArray(section.data) && (
              <div className="array-data-container">
                {section.data.length === 0 ? (
                  <Text size="2" className="empty-data">
                    No {section.title.toLowerCase()} data
                  </Text>
                ) : (
                  section.data.map((item, itemIndex) => (
                    <div key={itemIndex} className="array-item">
                      <Text
                        size="3"
                        weight="medium"
                        className="array-item-title">
                        {section.title.slice(0, -1)} {itemIndex + 1}
                      </Text>
                      <div className="employee-details-styled">
                        {Object.entries(item)
                          .filter(([key, value]) => {
                            if (typeof value === "string" && isUUID(value))
                              return false;
                            return true;
                          })
                          .map(([key, value]) => (
                            <div key={key} className="employee-detail-row">
                              <Text
                                weight="medium"
                                className="employee-detail-label">
                                {key.charAt(0).toUpperCase() +
                                  key.slice(1).replace(/([A-Z])/g, " $1")}
                              </Text>
                              <Text
                                className={`employee-detail-value ${typeof value === "object" && value !== null ? "object-value" : ""} ${key.toLowerCase() === "avatar" ? "avatar-value" : ""}`}>
                                {formatValue(value)}
                              </Text>
                            </div>
                          ))}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {section.isObject && section.data && (
              <div className="object-data-container">
                {Object.keys(section.data).length === 0 ? (
                  <Text size="2" className="empty-data">
                    No {section.title.toLowerCase()} data
                  </Text>
                ) : (
                  <div className="employee-details-styled">
                    {Object.entries(section.data)
                      .filter(([key, value]) => {
                        if (typeof value === "string" && isUUID(value))
                          return false;
                        return true;
                      })
                      .map(([key, value]) => (
                        <div key={key} className="employee-detail-row">
                          <Text
                            weight="medium"
                            className="employee-detail-label">
                            {key.charAt(0).toUpperCase() +
                              key.slice(1).replace(/([A-Z])/g, " $1")}
                          </Text>
                          <Text
                            className={`employee-detail-value ${typeof value === "object" && value !== null ? "object-value" : ""} ${key.toLowerCase() === "avatar" ? "avatar-value" : ""}`}>
                            {formatValue(value)}
                          </Text>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      // You could add a toast notification here
      console.log(`${label} copied to clipboard`);
    } catch (err) {
      console.error("Failed to copy to clipboard:", err);
    }
  };

  return (
    <div className="employees-table">
      <Table.Root size="2" variant="surface">
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeaderCell>Toggle</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Avatar</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Name</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Email</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Title</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Department</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Start Date</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {employees.map((employeeData) => (
            <React.Fragment key={employeeData.id}>
              <Table.Row>
                <Table.Cell>
                  <Button
                    variant="tertiary"
                    size="sm"
                    onClick={() => toggleRow(employeeData.id)}>
                    {expandedRows.has(employeeData.id) ? (
                      <ChevronUpIcon />
                    ) : (
                      <ChevronDownIcon />
                    )}
                  </Button>
                  
                  {/* <button
                    onClick={() => toggleRow(employeeData.id)}
                    className="row-toggle-button">
                    {expandedRows.has(employeeData.id) ? (
                      <ChevronUpIcon />
                    ) : (
                      <ChevronDownIcon />
                    )}
                  </button> */}
                </Table.Cell>
                <Table.Cell>
                  <div className="employee-avatar">
                    {employeeData.profile?.avatar ? (
                      <img
                        src={employeeData.profile.avatar}
                        alt={employeeData.profile.displayName || "Employee"}
                        className="avatar-image"
                      />
                    ) : (
                      <div className="avatar-placeholder">
                        {(
                          employeeData.profile?.displayName ||
                          employeeData.profile?.email
                        )
                          ?.charAt(0)
                          ?.toUpperCase() || "?"}
                      </div>
                    )}
                  </div>
                </Table.Cell>
                <Table.Cell>
                  <div className="employee-name">
                    {employeeData.profile?.displayName || "N/A"}
                  </div>
                </Table.Cell>
                <Table.Cell>
                  <div className="employee-email">
                    {employeeData.profile?.email}
                  </div>
                </Table.Cell>
                <Table.Cell>
                  <div className="employee-title">
                    {employeeData.profile?.title || "N/A"}
                  </div>
                </Table.Cell>
                <Table.Cell>
                  <div className="employee-department">
                    {employeeData.data?.department || "N/A"}
                  </div>
                </Table.Cell>
                <Table.Cell>
                  <div className="employee-start-date">
                    {employeeData.data?.startDate
                      ? formatDate(employeeData.data.startDate)
                      : "N/A"}
                  </div>
                </Table.Cell>
                <Table.Cell>
                  <div className="employee-status">
                    <Badge
                      color={
                        employeeData.data?.employmentStatus === "ACTIVE"
                          ? "green"
                          : "gray"
                      }>
                      {employeeData.data?.employmentStatus || "N/A"}
                    </Badge>
                  </div>
                </Table.Cell>
              </Table.Row>
              {expandedRows.has(employeeData.id) && (
                <Table.Row>
                  <Table.Cell colSpan={8}>
                    <Card className="employee-details-card">
                      <Tabs.Root defaultValue="profile">
                        <Tabs.List>
                          <Tabs.Trigger value="profile">Profile</Tabs.Trigger>
                          <Tabs.Trigger value="expanded">
                            HRIS Data
                          </Tabs.Trigger>
                          <Tabs.Trigger value="local">Local Data</Tabs.Trigger>
                          <Tabs.Trigger value="raw">Raw JSON</Tabs.Trigger>
                        </Tabs.List>
                        <Tabs.Content value="profile">
                          {renderStyledData(employeeData.profile)}
                        </Tabs.Content>
                        <Tabs.Content value="expanded">
                          {renderHRISData(employeeData.data)}
                        </Tabs.Content>
                        <Tabs.Content value="local">
                          {renderStyledData(employeeData)}
                        </Tabs.Content>
                        <Tabs.Content value="raw">
                          <div className="raw-json-container">
                            <div className="raw-json-section">
                              <div className="raw-json-header">
                                <Text size="3" weight="bold">
                                  Profile:
                                </Text>
                                <Button
                                 
                                  onClick={() =>
                                    copyToClipboard(
                                      JSON.stringify(
                                        employeeData.profile,
                                        null,
                                        2
                                      ),
                                      "Profile"
                                    )
                                  }
                                  className="copy-button">
                                  <CopyIcon width="14" height="14" />
                                </Button>
                              </div>
                              <pre className="employee-raw-json">
                                {JSON.stringify(employeeData.profile, null, 2)}
                              </pre>
                            </div>
                            <div className="raw-json-section">
                              <div className="raw-json-header">
                                <Text size="3" weight="bold">
                                  HRIS Data:
                                </Text>
                                <Button
                                 
                                  onClick={() =>
                                    copyToClipboard(
                                      JSON.stringify(
                                        employeeData.data,
                                        null,
                                        2
                                      ),
                                      "HRIS Data"
                                    )
                                  }
                                  className="copy-button">
                                  <CopyIcon width="14" height="14" />
                                </Button>
                              </div>
                              <pre className="employee-raw-json">
                                {JSON.stringify(employeeData.data, null, 2)}
                              </pre>
                            </div>
                            <div className="raw-json-section">
                              <div className="raw-json-header">
                                <Text size="3" weight="bold">
                                  Local Data:
                                </Text>
                                <Button
                                  
                                  onClick={() =>
                                    copyToClipboard(
                                      JSON.stringify(employeeData, null, 2),
                                      "Local Data"
                                    )
                                  }
                                  className="copy-button">
                                  <CopyIcon width="14" height="14" />
                                </Button>
                              </div>
                              <pre className="employee-raw-json">
                                {JSON.stringify(employeeData, null, 2)}
                              </pre>
                            </div>
                          </div>
                        </Tabs.Content>
                      </Tabs.Root>
                    </Card>
                  </Table.Cell>
                </Table.Row>
              )}
            </React.Fragment>
          ))}
        </Table.Body>
      </Table.Root>
      {employees.length === 0 && (
        <div className="employees-table-empty">No employees found.</div>
      )}
    </div>
  );
};
